/**
 * DocFreshnessWarnings — surface non-blocking vigencia warnings inline
 * inside each onboarding phase (company docs, representative, apoderado,
 * per-BO card).
 *
 * The backend is the SINGLE SOURCE OF TRUTH for vigencia thresholds
 * (see ``services/persona_moral.FRESHNESS_RULES_DAYS``). This component
 * never hardcodes day counts — it just queries
 * ``GET /api/customer-drafts/{draft_id}/document-warnings`` and renders
 * whatever the backend returns, optionally filtered by subject scope.
 *
 * Props:
 *   draftId          (string) — required
 *   subjectType      (string) — "company" | "representative" | "apoderado" | "beneficial_owner"
 *   subjectId        (string|null) — for per-subject filtering (e.g. "bo-1"). When null
 *                                    only ``subjectType`` is used as filter.
 *   refreshKey       (any) — change to force a refetch (e.g. after an upload completes)
 *   testIdSuffix     (string) — appended to data-testid so multiple cards on one page stay unique
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DocFreshnessWarnings({
  draftId,
  subjectType,
  subjectId = null,
  refreshKey = 0,
  testIdSuffix = "",
}) {
  const [warnings, setWarnings] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!draftId) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/customer-drafts/${draftId}/document-warnings`,
          { withCredentials: true },
        );
        if (!alive) return;
        const all = r.data?.warnings || [];
        const filtered = all.filter((w) => {
          if (subjectType && w.subject_type !== subjectType) return false;
          if (subjectId && w.subject_id !== subjectId) return false;
          return true;
        });
        setWarnings(filtered);
      } catch {
        if (alive) setWarnings([]);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [draftId, subjectType, subjectId, refreshKey]);

  if (!loaded || warnings.length === 0) return null;

  const tid = `doc-warnings${testIdSuffix ? `-${testIdSuffix}` : ""}`;

  return (
    <div
      data-testid={tid}
      className="rounded-lg border border-amber-700/40 bg-amber-950/30 p-3 text-sm space-y-2"
    >
      <div className="flex items-start gap-2 text-amber-200">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
        <div className="flex-1">
          <div className="font-medium">
            Avisos de vigencia documental ({warnings.length})
          </div>
          <ul className="text-amber-200/80 text-xs mt-1 space-y-0.5">
            {warnings.map((w, i) => (
              <li
                key={`${w.doc_type}-${w.subject_id || "co"}-${i}`}
                data-testid={`${tid}-item`}
              >
                • {w.label}
              </li>
            ))}
          </ul>
          <div className="text-amber-200/60 text-xs pt-1">
            Estos avisos no impiden finalizar; quedan registrados como
            evidencia de revisión.
          </div>
        </div>
      </div>
    </div>
  );
}
